import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, tokens } from '../api/client';
import type { User } from '../api/types';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<User>;
  signupOrg: (payload: SignupOrgPayload) => Promise<User>;
  acceptInvite: (token: string, payload: AcceptInvitePayload) => Promise<User>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

export interface SignupOrgPayload {
  org_name: string;
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone?: string;
  org_type?: string;
  timezone?: string;
  invite?: string;
}

export interface AcceptInvitePayload {
  password: string;
  first_name: string;
  last_name: string;
  phone?: string;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    if (!tokens.getAccess()) {
      setUser(null);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser({ ...data.user, org_id: data.org_id });
    } catch {
      tokens.clear();
      setUser(null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await fetchMe();
      setLoading(false);
    })();
  }, [fetchMe]);

  const handleAuthResponse = useCallback((data: any): User => {
    tokens.set(data.access_token, data.refresh_token);
    const u: User = { ...data.user, org_id: data.org_id };
    setUser(u);
    return u;
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { data } = await api.post('/auth/login', { email, password });
    return handleAuthResponse(data);
  }, [handleAuthResponse]);

  const signupOrg = useCallback(async (payload: SignupOrgPayload) => {
    const { data } = await api.post('/auth/signup-org', payload);
    return handleAuthResponse(data);
  }, [handleAuthResponse]);

  const acceptInvite = useCallback(async (token: string, payload: AcceptInvitePayload) => {
    const { data } = await api.post(`/auth/invite/${token}/accept`, payload);
    return handleAuthResponse(data);
  }, [handleAuthResponse]);

  const logout = useCallback(() => {
    tokens.clear();
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    await fetchMe();
  }, [fetchMe]);

  const value = useMemo(
    () => ({ user, loading, login, signupOrg, acceptInvite, logout, refreshUser }),
    [user, loading, login, signupOrg, acceptInvite, logout, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
